import React from 'react';
import {
  Card,
  CondensedHeader,
  Gutter,
  SmallHeader,
  CardHeader,
  ProgressSteps,
  ProgressStepsItem,
  SectionHeader,
  Layout,
  LayoutItem,
  Paragraph,
  CondensedLarge,
  ParagraphHeader,
  CardSection,
  DateUtil,
  PluralizeUtil,
} from '@axiom/ui';
import { WorkFeedConst } from '@axiom/const';
import type { CandidateWorkFeedItem } from '@axiom/validation';
import moment from 'moment';

const { Phases } = WorkFeedConst;

export const OpportunityPhaseTracker = ({
  workFeedItem,
  name = 'OPPORTUNITY_PHASE_TRACKER',
}: {
  workFeedItem: CandidateWorkFeedItem;
  name?: string;
}) => {
  const {
    runningCandidateInterestedCount,
    runningCandidateInterviewingCount,
    runningCandidateSubmittedCount,
    phase,
    phaseInterviewingDate,
    phaseSubmittedDate,
    opportunity: { madeAvailableToFeedAt, closedDate },
  } = workFeedItem;

  const getDaysAvailable = (fromDate: moment.MomentInput) => {
    return fromDate ? moment(new Date()).diff(moment(fromDate), 'days') : '0';
  };

  return (
    <Card name={name}>
      <CardHeader>
        <SmallHeader name="TRACKER_HEADER">
          Opportunity Stage Tracker
        </SmallHeader>
      </CardHeader>
      <CardSection>
        <ProgressSteps
          name="PROGRESS_STEPS"
          completedStep={phase}
          direction="vertical"
          icon="checkmark"
        >
          <ProgressStepsItem stepName={Phases.New}>
            <Layout stackableOn="mobile">
              <LayoutItem fluid>
                <SectionHeader name="SECTION_HEADER_NEW">New</SectionHeader>
              </LayoutItem>
              <LayoutItem>
                <CondensedHeader name="date-new">
                  {DateUtil.displayDate(madeAvailableToFeedAt)}
                </CondensedHeader>
              </LayoutItem>
            </Layout>
            <Gutter bottom="8px" />
            <Paragraph name="days-opened-new">
              Axiom has not yet submitted any talent for this opportunity. This
              opportunity has been open for{' '}
              {getDaysAvailable(madeAvailableToFeedAt)}{' '}
              {PluralizeUtil({
                text: 'day',
                count: Number(getDaysAvailable(madeAvailableToFeedAt)),
              })}
              .
            </Paragraph>
            {!workFeedItem.opportunity.selfService && (
              <CondensedLarge name="count-subtitle-new">
                <ParagraphHeader name="running-interested-new" as="span">
                  {runningCandidateInterestedCount}
                </ParagraphHeader>{' '}
                {PluralizeUtil({
                  text: 'talent',
                  count: runningCandidateInterestedCount,
                  options: { one: 'talent is', many: 'talent are' },
                })}{' '}
                interested in this opportunity.
              </CondensedLarge>
            )}
          </ProgressStepsItem>
          <ProgressStepsItem stepName={Phases.Submitted}>
            <Layout stackableOn="mobile">
              <LayoutItem fluid>
                <SectionHeader name="SECTION_HEADER_SUBMITTING">
                  Submitting
                </SectionHeader>
              </LayoutItem>
              <LayoutItem>
                <CondensedHeader name="date-submitting">
                  {DateUtil.displayDate(phaseSubmittedDate)}
                </CondensedHeader>
              </LayoutItem>
            </Layout>
            <Gutter bottom="8px" />
            <Paragraph>
              Talent profiles are currently being submitted for the client’s
              consideration.
            </Paragraph>
            <CondensedLarge name="count-subtitle-submitting">
              <ParagraphHeader name="running-submitting" as="span">
                {runningCandidateSubmittedCount}
              </ParagraphHeader>{' '}
              {PluralizeUtil({
                text: 'talent profile',
                count: runningCandidateSubmittedCount,
                options: {
                  one: 'talent profile has',
                  many: 'talent profiles have',
                },
              })}{' '}
              been submitted for this opportunity.
            </CondensedLarge>
          </ProgressStepsItem>
          <ProgressStepsItem stepName={Phases.Interviewing}>
            <Layout stackableOn="mobile">
              <LayoutItem fluid>
                <SectionHeader name="SECTION_HEADER_INTERVIEWING">
                  Interviewing
                </SectionHeader>
              </LayoutItem>
              <LayoutItem>
                <CondensedHeader name="date-interviewing">
                  {DateUtil.displayDate(phaseInterviewingDate)}
                </CondensedHeader>
              </LayoutItem>
            </Layout>
            <Gutter bottom="8px" />
            <Paragraph>
              The client is actively interviewing talent who were submitted. If
              none are selected Axiom will consider additional profiles.
            </Paragraph>
            <CondensedLarge name="count-subtitle-interviewing">
              <ParagraphHeader name="running-interviewing-count" as="span">
                {runningCandidateInterviewingCount}
              </ParagraphHeader>{' '}
              {PluralizeUtil({
                text: 'talent',
                count: runningCandidateInterviewingCount,
                options: { one: 'talent is', many: 'talent are' },
              })}{' '}
              interviewing for this opportunity.
            </CondensedLarge>
          </ProgressStepsItem>
          <ProgressStepsItem stepName={Phases.Closed}>
            <Layout stackableOn="mobile">
              <LayoutItem fluid>
                <SectionHeader name="SECTION_HEADER_CLOSED">
                  Closed
                </SectionHeader>
              </LayoutItem>
              <LayoutItem>
                <CondensedHeader name="date-closed">
                  {DateUtil.displayDate(closedDate)}
                </CondensedHeader>
              </LayoutItem>
            </Layout>
            <Gutter bottom="8px" />
            <Paragraph>
              The client has selected Axiom talent for this role and the
              opportunity is no longer available.
            </Paragraph>
          </ProgressStepsItem>
        </ProgressSteps>
      </CardSection>
    </Card>
  );
};
